import QFields from './QFields';
import QUnitCollection from './QUnitCollection.js';
import FieldsMapping from './fields_mapping';
import { QFilters, get_qfilter } from '@/qunit/filters';

let next_instance_id = 1;

export default class QUnit {
  constructor(data) {
    // Map all qunit keys to this
    this._keys = [];

    if (data instanceof QUnit) {
      for (const key of data._keys) {
        this[key] = data[key];
      }
      this._keys = data._keys;

    } else {
      for (const [key, value] of Object.entries(data)) {
        this._keys.push(key);
        this[key] = value;
      }

      if (!this.uid)
        throw "A QUnit must always have an UID";
    }

    try {
      this.post_parse();
    } catch (err) {
      console.error(err);
      throw err;
    }

    this._generate_instance_id();
    // console.warn('-> QUnit(', this, ')'); // DEBUG
  }

  overwrite(data) {
    // Overwrite current properties with data and return a new QUnitCollection.
    return new QUnit({...this.serialize(), ...data});
  }

  make_qfield(field) {
    const type = (field instanceof QFields.QUnitField) ? field.props.type : field.type;
    return new (FieldsMapping.apitype2field(field.type))(field);
  }

  post_parse() {
    // Parse this entries
    this.parse_site();
    this.parse_fields();
    this.parse_workflow();
    this.parse_relations();
    this.parse_comunications();
    this.parse_inline_boxes();
    this.parse_business_process();
  }

  parse_site() {
    if (!this.site) {
      this.site = +this.uid.split('#')[2];
    }
  }

  parse_business_process() {
    if (this.business_process) {
      this.fields.business_process_name = new QFields.QUnitFieldString({value:this.business_process_name});
    } else {
      this.business_process = this.uid.split('#')[1];
    }
  }

  parse_fields() {
    // WARNING: 
    // There's a client using centro (before we created it) in all his BPs models.
    // It's overwriting our centro param every time, so we are protecting this param 
    this._protected = { centro: this.centro };
    if (!this.fields) {
      return;
    }
    const neo = {};
    for (const [key, props] of Object.entries(this.fields)) {
      if (props instanceof QFields.QUnitField) {
        neo[key] = this.make_qfield({...props.props, id: key});
      } else {
        neo[key] = this.make_qfield({ ...props, id: key });
      }
    }

    this.fields = neo;

    // Define getters for root QUnit properties forwarded to this.fields
    for (const key of Object.keys(this.fields)) {  
      Object.defineProperty(this, key, { get: () => this.fields[key] });
    }
  }

  parse_workflow() {
    let cols = [];
    if (this.inline_boxes && this.inline_boxes.length > 0) {
      const inline_workflow = this.inline_boxes.filter(
        x => x.type == 'inline-box-workflow',
      );
      if (inline_workflow.length > 0)
        cols = inline_workflow[0].columns.map(v => v.value);
    }

    // Fields at this.workflow[].fields
    if (!this.workflow || this.workflow.length <= 0)
      return;

    for (const wf of this.workflow) {
      for (const wf_key of Object.keys(wf)) {
        if (wf_key === 'fields') {
          // for (const key of Object.keys(wf.fields))
          //   wf.fields[key] = this.make_qfield(wf.fields[key]);
          for (const [key, props] of Object.entries(wf.fields)) {
            if (props instanceof QFields.QUnitField) {
              wf.fields[key] = this.make_qfield({...props.props, id: key});
            } else {
              wf.fields[key] = this.make_qfield({ ...props, id: key });
            }
          }

          // Define getters for workflow properties forwarded to this.fields
          for (const key of Object.keys(wf.fields)) {
            Object.defineProperty(wf, key, { get: () => wf.fields[key], configurable: true });
          }

        } else if ((wf.fields||{})[wf_key] === undefined) {
          if (cols.includes(wf_key)) {            
            try {
              if (wf[wf_key] instanceof QFields.QUnitField) {
                wf[wf_key] = this.make_qfield({...wf[wf_key].props, id: wf_key});
              } else {
                wf[wf_key] = this.make_qfield({ ...wf[wf_key], id: wf_key });
              }
            } catch (error) {
              wf[wf_key] = wf[wf_key];
            }

          } else {
            // wf[wf_key] = wf[wf_key];
          }
        }
      }
    }
  }

  parse_inline_boxes() {
    // Fields at this.inline_boxes[id=='data'].props.layout[].rows[][]

    if (!this.inline_boxes || this.inline_boxes.length <= 0)
      return
    for (const ibd of this.inline_boxes.filter(b => b.type === 'inline-box-data')) {
      for (const layout of ibd.props.layout) {
        layout.rows = layout.rows.map(row => (
          row
            .filter(cell => !!this.fields[cell.id] || cell)
            .map(cell =>
              this.fields[cell.id]
                ? this.fields[cell.id].clone(cell)
                : this.make_qfield(cell),
            )
        ));
      }
    }

    // Related as QUnitCollection
    let now_qunits = null;
    for (const ibr of this.inline_boxes.filter(b => (b.type === 'inline-box-related'
                                                     || b.type === 'inline-box-indicator-tracking'))) {
      now_qunits = this.relations;
      let filters = new QFilters();
      if (ibr.props.filter) {
        filters.set_filter(get_qfilter('business_process', ibr.props.filter))
        // TODO: Enrich the filter here by using a dictionary instead of only BP
        now_qunits = now_qunits.filter(filters);
      }
      if (ibr.props.filter_include) {
        filters.set_filter(get_qfilter('business_process_include', ibr.props.filter_include))
        now_qunits = now_qunits.filter(filters);
      }
      if (ibr.props.filter_exclude) {
        filters.set_filter(get_qfilter('business_process_exclude', ibr.props.filter_exclude))
        now_qunits = now_qunits.filter(filters);
      }

      // Overwrite with ibr header
      ibr.qunits = now_qunits.overwrite({
        columns: ibr.props.headers.map(h => ({...h, key: h.value})),
      });
    }
  }

  parse_relations() {
    if (this.relations instanceof QUnitCollection) {
      this.relations = new QUnitCollection(this.relations);
    } else {
      this.relations = new QUnitCollection({
        units: this.relations === undefined ? [] : this.relations,
      });
    }
  }

  parse_comunications() {
    if (this.comunications instanceof QUnitCollection) {
      this.comunications = new QUnitCollection(this.comunications);
    } else {
      let cols = [
        { value: 'title', text: 'Asunto', sortable: true },
        { value: 'creador', text: 'Emisor', sortable: true },
        { value: 'receptor', text: 'Destinatario', sortable: true },
        { value: 'fecha_creacion', text: 'Fecha comunicado', sortable: true },
        { value: 'fecha_lectura', text: 'Fecha lectura', sortable: true },
        { value: 'current_workflow_state', text: 'Estado', sortable: true },
        { value: 'readable_type', text: 'Tipo', sortable: true },
        { value:'data-table-expand', text: '', sortable: false}
      ];
      if (this.inline_boxes && this.inline_boxes.length > 0) {
        const inline_comunications = this.inline_boxes.filter(
          x => x.type == 'inline-box-comunications',
        );
        if (inline_comunications.length > 0){
          cols =  inline_comunications[0].columns
            ? inline_comunications[0].columns.map(h => ({...h, key: h.value}))
            : cols;
        }

      }
      this.comunications = new QUnitCollection({
        units: this.comunications === undefined ? [] : this.comunications,
        labels: cols,
      });
    }
  }

  toJSON() {
    return this.serialize();
  }

  serialize() {
    const dict = {};
    for (const key of this._keys) {
      dict[key] = this[key];
    }
    return dict;
  }

  _generate_instance_id() {
    // Generate a unique identifier
    this.instance_id = next_instance_id++; // Vue reactive winks
  }
}
